import { useContext, useState, useEffect } from 'react';
import { Eye, Search, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import adminAuditService from '../../../../../../services/audit_services/adminAuditService';
import Pagination from '../../../../../../components/Pagination';

export default function AdminRecommendationActionsList() {
  const { authTokens } = useContext(AuthContext);
  const { showError } = useToast();

  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [ordering, setOrdering] = useState('created_at_desc');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    fetchActions();
  }, []);

  useEffect(() => {
    if (currentPage > 1) {
      fetchActions();
    }
  }, [currentPage]);

  const fetchActions = async () => {
    setLoading(true);
    
    const params = {
      search: searchTerm,
      status: statusFilter,
      ordering: ordering,
      page: currentPage,
      page_size: pageSize
    };
    
    const result = await adminAuditService.getRecommendationActions(authTokens.access, params);
    
    if (result.success) {
      setActions(result.data.results || []);
      setTotalPages(result.data.total_pages || 1);
      setTotalCount(result.data.count || 0);
    } else {
      showError(result.error || 'Erreur lors du chargement des actions de recommandation');
      setActions([]);
    }
    
    setLoading(false);
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchActions();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setOrdering('created_at_desc');
    setCurrentPage(1);
    setTimeout(() => fetchActions(), 0);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'En attente' },
      in_progress: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'En cours' },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Complété' },
      achieved: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Archivé' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des actions de recommandation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <CheckCircle className="h-8 w-8 text-green-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Actions de recommandation</h1>
          <p className="text-sm text-gray-600 mt-1">Consultez toutes les actions de recommandation de l'organisation</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rechercher
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleApplyFilters()}
                placeholder="Texte de recommandation, éléments, question..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Statut
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">Tous</option>
              <option value="pending">En attente</option>
              <option value="in_progress">En cours</option>
              <option value="completed">Complété</option>
              <option value="achieved">Archivé</option>
            </select>
          </div>

          {/* Ordering */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Trier par
            </label>
            <select
              value={ordering}
              onChange={(e) => setOrdering(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="created_at_desc">Plus récent</option>
              <option value="created_at_asc">Plus ancien</option>
              <option value="status_asc">Statut (A-Z)</option>
              <option value="status_desc">Statut (Z-A)</option>
              <option value="delay_asc">Échéance (proche)</option>
              <option value="delay_desc">Échéance (lointaine)</option>
            </select>
          </div>
        </div>

        {/* Buttons */}
        <div className="mt-4 flex justify-end space-x-3">
          <button
            onClick={handleClearFilters}
            className="inline-flex items-center px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Effacer
          </button>
          <button
            onClick={handleApplyFilters}
            className="inline-flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Search className="h-4 w-4 mr-2" />
            Appliquer
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {actions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Aucune action de recommandation trouvée</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recommandation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Question
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Propriétaire
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Échéance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date de création
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {actions.map((action) => (
                  <tr key={action.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-gray-900 line-clamp-2">{action.recommendation_text}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">{action.question_name || 'N/A'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">{action.recommendation_owner_name}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">
                        {action.delay_of_recommendation 
                          ? new Date(action.delay_of_recommendation).toLocaleDateString('fr-FR')
                          : 'Non défini'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(action.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">
                        {new Date(action.created_at).toLocaleDateString('fr-FR')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <Link
                        to={`/dashboard/admin/audits/recommendation-actions/${action.id}`}
                        className="inline-flex items-center px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Voir
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalCount={totalCount}
          pageSize={pageSize}
        />
      )}
    </div>
  );
}
